/**
 * HubSpot developer-mode form enhancement.
 *
 * Marks rendered HubSpot forms with extra DOM hooks that pure CSS can't
 * reach because nested `:has(...:not(:has(...)))` is not a valid CSS
 * selector. Specifically:
 *
 *   - `.thrive-hs-section-title`  — rows whose only child is a non-link
 *                                   .hsfc-RichText (i.e. "YOUR CONTACT
 *                                   DETAILS", "BUILDING DETAILS"…). The
 *                                   SCSS then draws HR-like top borders on
 *                                   every section title except the first.
 *   - `.thrive-hs-legal`          — rows whose RichText contains a link
 *                                   (i.e. the "By submitting…" disclaimer).
 *                                   The SCSS hides this only on
 *                                   .hs-form-html--parents (which renders
 *                                   its own disclaimer outside the form).
 *
 * Also strips trailing `*` from input placeholders (CF7 didn't include them
 * in placeholders; the asterisk is a HubSpot "required" convention we'd
 * rather express via CSS / aria only).
 *
 * Submit button text is wrapped in `<span class="thrive-hs-btn-label">` so
 * the SCSS can give it `position: relative; z-index: 1` and sit above the
 * circle-swoop `::before` (the same hover effect as CF7's `.btn--filled`).
 * Without the wrapper, the bare text node inside `<button>` paints below the
 * positioned `::before` pseudo and gets covered when the violet swoop rises.
 *
 * HubSpot's developer-mode embed renders asynchronously after the bundle
 * fetches the form schema, so we use a MutationObserver to enhance forms
 * as they appear.
 */

const SECTION_TITLE_CLASS = 'thrive-hs-section-title';
const LEGAL_CLASS = 'thrive-hs-legal';
const BTN_LABEL_CLASS = 'thrive-hs-btn-label';

function enhanceRow( row ) {
	if ( row.dataset.thriveHsEnhanced === '1' ) return;

	const rt = row.querySelector( '.hsfc-RichText' );
	if ( ! rt ) return;

	row.dataset.thriveHsEnhanced = '1';
	if ( rt.querySelector( 'a' ) ) {
		row.classList.add( LEGAL_CLASS );
		fixLegalLinks( rt );
	} else {
		row.classList.add( SECTION_TITLE_CLASS );
	}
}

/**
 * Correct the Terms & Conditions link inside the HubSpot consent RichText.
 * The form was authored in HubSpot with `/terms-and-conditions` (a slug that
 * 404s — the live page is `/terms-conditions/`). We can't edit the HubSpot
 * portal from here, so rewrite the href client-side. Idempotent and a no-op
 * once HubSpot is corrected at the source.
 */
function fixLegalLinks( rt ) {
	rt.querySelectorAll( 'a[href*="terms-and-conditions"]' ).forEach( ( a ) => {
		a.setAttribute(
			'href',
			a.getAttribute( 'href' ).replace( '/terms-and-conditions', '/terms-conditions' )
		);
	} );
}

function stripAsterisk( el ) {
	if ( el.dataset.thriveHsStripped === '1' ) return;
	el.dataset.thriveHsStripped = '1';
	const ph = el.getAttribute( 'placeholder' );
	if ( ph && ph.endsWith( '*' ) ) {
		el.setAttribute( 'placeholder', ph.replace( /\*+\s*$/, '' ).trimEnd() );
	}
}

/**
 * Wrap each bare text node inside the submit button in a <span> with the
 * `thrive-hs-btn-label` class. The SCSS gives that span `position: relative;
 * z-index: 1` so the label paints above the violet circle-swoop `::before`.
 *
 * HubSpot may swap the button label during the submit lifecycle ("Submit" →
 * "Submitting…" → done), so we skip nodes that are already wrapped and
 * re-process any new text nodes that appear on subsequent ticks.
 */
function wrapBtnLabel( btn ) {
	if ( btn.dataset.thriveHsBtn === '1' && btn.querySelector( '.' + BTN_LABEL_CLASS ) ) return;
	btn.dataset.thriveHsBtn = '1';

	Array.from( btn.childNodes ).forEach( ( node ) => {
		if ( node.nodeType === 3 /* text */ && node.nodeValue.trim() ) {
			const span = document.createElement( 'span' );
			span.className = BTN_LABEL_CLASS;
			span.textContent = node.nodeValue;
			btn.replaceChild( span, node );
		}
	} );
}

function enhance( root = document ) {
	root.querySelectorAll( '.hs-form-html .hsfc-Step__Content > .hsfc-Row' ).forEach( enhanceRow );
	root.querySelectorAll( '.hs-form-html input[placeholder], .hs-form-html textarea[placeholder]' ).forEach( stripAsterisk );
	root.querySelectorAll( '.hs-form-html .hsfc-Button' ).forEach( wrapBtnLabel );
}

export const HubspotFormEnhance = {
	init() {
		if ( ! document.querySelector( '.hs-form-html' ) ) return;

		// Run once for anything already present, then watch for HubSpot's
		// async render to inject the form internals.
		enhance();

		const observer = new MutationObserver( ( mutations ) => {
			for ( const mutation of mutations ) {
				for ( const node of mutation.addedNodes ) {
					if ( node.nodeType !== 1 ) continue;
					if ( node.matches?.( '.hs-form-html' ) || node.closest?.( '.hs-form-html' ) ) {
						enhance();
						return;
					}
				}
			}
		} );

		document.querySelectorAll( '.hs-form-html' ).forEach( ( wrap ) => {
			observer.observe( wrap, { childList: true, subtree: true } );
		} );
	},
};
